import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Lock, Unlock, CheckCircle2, XCircle, Plus, Trash2, RefreshCw, Banknote, Coins } from 'lucide-react';
import { theme, cardStyle, inputStyle, labelStyle, tdStyle, thStyle, btnStyle, statCard } from '../theme';

const fmtINR = n => `₹${(Number(n)||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

const DENOMS = [
    { k:'note2000', l:'₹2000', v:2000, color:'#ec4899' },
    { k:'note500',  l:'₹500',  v:500,  color:'#8b5cf6' },
    { k:'note200',  l:'₹200',  v:200,  color:'#f59e0b' },
    { k:'note100',  l:'₹100',  v:100,  color:'#3b82f6' },
    { k:'note50',   l:'₹50',   v:50,   color:'#10b981' },
    { k:'note20',   l:'₹20',   v:20,   color:'#f97316' },
    { k:'note10',   l:'₹10',   v:10,   color:'#64748b' },
    { k:'coin10',   l:'₹10c',  v:10,   color:'#64748b' },
    { k:'coin5',    l:'₹5',    v:5,    color:'#6b7280' },
    { k:'coin2',    l:'₹2',    v:2,    color:'#6b7280' },
    { k:'coin1',    l:'₹1',    v:1,    color:'#6b7280' },
];

export default function DayEndPage({ API_BASE, currentUser }) {
    const [date, setDate] = useState(todayStr());
    const [closure, setClosure] = useState(null);
    const [petty, setPetty] = useState([]);
    const [busy, setBusy] = useState(false);
    const [pettyForm, setPettyForm] = useState(null);
    const [msg, setMsg] = useState({ type:'', text:'' });

    const refresh = useCallback(async () => {
        try {
            const [c, p] = await Promise.all([
                axios.get(`${API_BASE}/day-end/closure?date=${date}`),
                axios.get(`${API_BASE}/day-end/petty-cash?date=${date}`),
            ]);
            setClosure(c.data); setPetty(p.data);
        } catch (e) { setMsg({ type:'error', text: e.response?.data?.error || 'Failed' }); }
    }, [API_BASE, date]);
    useEffect(() => { refresh(); }, [refresh]);

    const updateDenomination = (k, v) => {
        setClosure(c => ({ ...c, denominations: { ...c.denominations, [k]: Number(v)||0 } }));
    };

    const saveCount = async () => {
        setBusy(true); setMsg({ type:'', text:'' });
        try {
            const r = await axios.put(`${API_BASE}/day-end/closure/${closure._id}`, {
                denominations: closure.denominations,
                openingCash: closure.openingCash,
                closingFloat: closure.closingFloat,
                notes: closure.notes,
            });
            setClosure(r.data);
            setMsg({ type:'success', text:'Count saved' });
        } catch (e) { setMsg({ type:'error', text: e.response?.data?.error || 'Save failed' }); }
        setBusy(false);
    };

    const closeDay = async () => {
        if (!closure.cashierName && !currentUser) return setMsg({ type:'error', text:'Cashier name required' });
        if (!window.confirm(`Close day ${date}? The day becomes immutable — petty cash and bills for this date cannot be edited.`)) return;
        setBusy(true);
        try {
            const r = await axios.post(`${API_BASE}/day-end/closure/${closure._id}/close`, {
                cashierName:    closure.cashierName || currentUser?.fullName,
                supervisorName: closure.supervisorName,
                counted:        closure.countedCash,
                denominations:  closure.denominations,
                closingFloat:   closure.closingFloat,
                notes:          closure.notes,
                user:           currentUser?.username,
            });
            setClosure(r.data);
            setMsg({ type:'success', text:'Day closed ✓' });
        } catch (e) { setMsg({ type:'error', text: e.response?.data?.error || 'Close failed' }); }
        setBusy(false);
    };

    const reopenDay = async () => {
        const reason = window.prompt('Reason for reopening (required):');
        if (!reason) return;
        setBusy(true);
        try {
            const r = await axios.post(`${API_BASE}/day-end/closure/${closure._id}/reopen`, { reason, user: currentUser?.username });
            setClosure(r.data);
            setMsg({ type:'success', text:'Day reopened' });
        } catch (e) { setMsg({ type:'error', text: e.response?.data?.error || 'Failed' }); }
        setBusy(false);
    };

    const savePetty = async () => {
        try {
            await axios.post(`${API_BASE}/day-end/petty-cash`, { ...pettyForm, date, addedBy: currentUser?.username });
            setPettyForm(null); refresh();
        } catch (e) { setMsg({ type:'error', text: e.response?.data?.error || 'Failed' }); }
    };
    const delPetty = async (id) => {
        if (!window.confirm('Delete this entry?')) return;
        try { await axios.delete(`${API_BASE}/day-end/petty-cash/${id}`); refresh(); }
        catch (e) { alert(e.response?.data?.error || 'Failed'); }
    };

    if (!closure) return <div style={{padding:20}}>Loading…</div>;

    const locked = closure.status === 'closed';
    const varianceColor = closure.variance === 0 ? theme.success : Math.abs(closure.variance) < 10 ? theme.warning : theme.danger;
    const countedByDenom = DENOMS.reduce((s,d) => s + (Number(closure.denominations?.[d.k]) || 0) * d.v, 0);

    return (
        <div>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'18px'}}>
                <div>
                    <h2 style={{margin:0, color:theme.navy, fontSize:'1.4rem', fontWeight:'700'}}>Day-End Closure</h2>
                    <p style={{margin:'2px 0 0', color:theme.textMuted, fontSize:'0.85rem'}}>Cash reconciliation · Petty cash · Signed closure</p>
                </div>
                <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
                    <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={{...inputStyle, marginBottom:0, width:'170px'}}/>
                    <button onClick={refresh} style={{padding:'8px 14px', borderRadius:'8px', border:`1px solid ${theme.border}`, backgroundColor:'white', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px', fontWeight:'600', fontSize:'0.82rem'}}>
                        <RefreshCw size={14}/> Refresh
                    </button>
                </div>
            </div>

            {msg.text && (
                <div style={{padding:'10px 14px', borderRadius:'8px', marginBottom:'14px',
                    backgroundColor: msg.type==='error' ? '#fef2f2' : '#f0fdf4',
                    color: msg.type==='error' ? theme.danger : theme.success, fontSize:'0.86rem', fontWeight:'500'}}>
                    {msg.text}
                </div>
            )}

            {/* Status banner */}
            <div style={{...cardStyle, marginBottom:'18px', display:'flex', alignItems:'center', gap:'14px',
                borderLeft:`5px solid ${locked?theme.success:theme.warning}`,
                backgroundColor: locked?'#f0fdf4':'#fffbeb'}}>
                {locked ? <Lock size={22} color={theme.success}/> : <Unlock size={22} color={theme.warning}/>}
                <div style={{flex:1}}>
                    <div style={{fontWeight:'700', color:theme.navy}}>{locked ? `Closed at ${new Date(closure.closedAt).toLocaleString('en-IN')}` : 'Day is OPEN — finalize by closing below'}</div>
                    {locked && closure.cashierName && <div style={{fontSize:'0.8rem', color:theme.textMuted}}>Cashier: {closure.cashierName}{closure.supervisorName?` · Supervisor: ${closure.supervisorName}`:''}</div>}
                </div>
                {locked && <button onClick={reopenDay} style={{...btnStyle, marginTop:0, backgroundColor:theme.warning, padding:'8px 16px', fontSize:'0.85rem'}}>Reopen</button>}
            </div>

            {/* Top KPIs — system figures */}
            <div style={{display:'grid', gridTemplateColumns:'repeat(6, 1fr)', gap:'10px', marginBottom:'18px'}}>
                {[
                    {l:'Bills', v:closure.billsCount, c:theme.navy},
                    {l:'Cash', v:fmtINR(closure.systemCash), c:theme.success},
                    {l:'Card', v:fmtINR(closure.systemCard), c:theme.info},
                    {l:'UPI', v:fmtINR(closure.systemUpi), c:'#8b5cf6'},
                    {l:'Bank', v:fmtINR(closure.systemBank), c:'#0ea5e9'},
                    {l:'Total', v:fmtINR(closure.systemTotal), c:theme.primary},
                ].map((s,i) => (
                    <div key={i} style={statCard}>
                        <span style={{fontSize:'0.72rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>{s.l}</span>
                        <h3 style={{margin:'4px 0 0', color:s.c, fontSize:'1.05rem', fontWeight:'700'}}>{s.v}</h3>
                    </div>
                ))}
            </div>

            <div style={{display:'grid', gridTemplateColumns:'1.2fr 1fr', gap:'16px', marginBottom:'18px'}}>
                {/* Denomination count */}
                <div style={cardStyle}>
                    <h3 style={{margin:'0 0 12px', color:theme.navy, fontSize:'1rem', display:'flex', alignItems:'center', gap:'8px'}}>
                        <Banknote size={18}/> Cash Count by Denomination
                    </h3>
                    <div style={{display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:'10px'}}>
                        {DENOMS.map(d => {
                            const n = Number(closure.denominations?.[d.k]) || 0;
                            const sub = n * d.v;
                            return (
                                <div key={d.k} style={{display:'flex', alignItems:'center', gap:'8px', padding:'8px 10px', backgroundColor:'#f8fafc', borderRadius:'8px'}}>
                                    <div style={{width:40, padding:'4px', borderRadius:'4px', backgroundColor:d.color, color:'white', textAlign:'center', fontSize:'0.7rem', fontWeight:'700'}}>{d.l}</div>
                                    <span style={{fontSize:'0.82rem', color:theme.textMuted}}>×</span>
                                    <input type="number" disabled={locked} value={n || ''} onChange={e=>updateDenomination(d.k, e.target.value)}
                                        style={{...inputStyle, marginBottom:0, width:60, fontSize:'0.88rem', textAlign:'center'}}/>
                                    <span style={{fontWeight:'700', color:theme.navy, fontSize:'0.86rem', marginLeft:'auto'}}>{fmtINR(sub)}</span>
                                </div>
                            );
                        })}
                    </div>
                    <div style={{marginTop:'14px', padding:'10px 14px', backgroundColor:theme.primary+'18', borderRadius:'8px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                        <span style={{fontWeight:'600', color:theme.navy}}>Counted Total</span>
                        <span style={{fontWeight:'700', color:theme.primary, fontSize:'1.2rem'}}>{fmtINR(countedByDenom)}</span>
                    </div>
                </div>

                {/* Reconciliation */}
                <div style={cardStyle}>
                    <h3 style={{margin:'0 0 12px', color:theme.navy, fontSize:'1rem', display:'flex', alignItems:'center', gap:'8px'}}>
                        <Coins size={18}/> Cash Reconciliation
                    </h3>
                    <Row label="Opening Cash (float)" value={<input disabled={locked} type="number" value={closure.openingCash||0} onChange={e=>setClosure(c=>({...c, openingCash:Number(e.target.value)||0}))} style={{...inputStyle, marginBottom:0, width:110, textAlign:'right'}}/>}/>
                    <Row label="+ Cash sales today" value={fmtINR(closure.systemCash)}/>
                    <Row label="− Cash expenses" value={fmtINR(closure.cashExpenses)} neg/>
                    <Row label="− Bank deposits" value={fmtINR(closure.bankDeposit)} neg/>
                    <div style={{height:1, backgroundColor:theme.border, margin:'10px 0'}}/>
                    <Row label="Expected Cash" value={<strong>{fmtINR(closure.expectedCash)}</strong>} big/>
                    <Row label="Counted Cash" value={<strong>{fmtINR(closure.countedCash)}</strong>} big/>
                    <div style={{marginTop:'10px', padding:'10px 14px', borderRadius:'8px', backgroundColor: varianceColor+'18'}}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                            <span style={{color:varianceColor, fontWeight:'700'}}>Variance</span>
                            <span style={{color:varianceColor, fontWeight:'700', fontSize:'1.15rem'}}>{closure.variance > 0 ? '+' : ''}{fmtINR(closure.variance)}</span>
                        </div>
                    </div>
                    <div style={{marginTop:'12px'}}>
                        <label style={labelStyle}>Closing Float (carry to tomorrow)</label>
                        <input type="number" disabled={locked} value={closure.closingFloat||0} onChange={e=>setClosure(c=>({...c, closingFloat:Number(e.target.value)||0}))} style={inputStyle}/>
                    </div>
                </div>
            </div>

            {/* Petty cash */}
            <div style={{...cardStyle, marginBottom:'18px'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px'}}>
                    <h3 style={{margin:0, color:theme.navy}}>Petty Cash / Bank Deposits ({petty.length})</h3>
                    {!locked && <button onClick={()=>setPettyForm({ category:'expense', amount:0, description:'', recipient:'' })} style={{...btnStyle, marginTop:0, padding:'7px 14px', fontSize:'0.82rem'}}>+ Add Entry</button>}
                </div>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.84rem'}}>
                    <thead><tr>
                        <th style={thStyle}>Time</th><th style={thStyle}>Category</th>
                        <th style={thStyle}>Description</th><th style={thStyle}>Recipient</th>
                        <th style={thStyle}>Amount</th><th style={thStyle}></th>
                    </tr></thead>
                    <tbody>
                        {petty.map(p => (
                            <tr key={p._id}>
                                <td style={tdStyle}>{new Date(p.timestamp).toLocaleTimeString('en-IN', {hour:'2-digit', minute:'2-digit'})}</td>
                                <td style={tdStyle}>
                                    <span style={{padding:'2px 8px', borderRadius:'10px', fontSize:'0.72rem', fontWeight:'700',
                                        backgroundColor: {expense:'#fee2e2','bank-deposit':'#dbeafe',withdrawal:'#fef3c7',refund:'#ede9fe'}[p.category]||'#e5e7eb',
                                        color: {expense:'#991b1b','bank-deposit':'#1e40af',withdrawal:'#92400e',refund:'#6d28d9'}[p.category]||'#374151'}}>{p.category}</span>
                                </td>
                                <td style={tdStyle}>{p.description}</td>
                                <td style={tdStyle}>{p.recipient || '—'}</td>
                                <td style={tdStyle}><strong>{fmtINR(p.amount)}</strong></td>
                                <td style={tdStyle}>{!p.locked && <button onClick={()=>delPetty(p._id)} style={{background:'none', border:'none', cursor:'pointer', color:theme.danger}}><Trash2 size={14}/></button>}</td>
                            </tr>
                        ))}
                        {petty.length === 0 && <tr><td colSpan="6" style={{...tdStyle, textAlign:'center', color:theme.textMuted, padding:'20px'}}>No petty cash entries</td></tr>}
                    </tbody>
                </table>
            </div>

            {/* Sign-off */}
            {!locked && (
                <div style={cardStyle}>
                    <h3 style={{margin:'0 0 12px', color:theme.navy}}>Sign Off & Close</h3>
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
                        <div>
                            <label style={labelStyle}>Cashier name *</label>
                            <input value={closure.cashierName||''} onChange={e=>setClosure(c=>({...c, cashierName:e.target.value}))} placeholder={currentUser?.fullName||''} style={inputStyle}/>
                        </div>
                        <div>
                            <label style={labelStyle}>Supervisor name (optional)</label>
                            <input value={closure.supervisorName||''} onChange={e=>setClosure(c=>({...c, supervisorName:e.target.value}))} style={inputStyle}/>
                        </div>
                        <div style={{gridColumn:'span 2'}}>
                            <label style={labelStyle}>Notes / Variance reason</label>
                            <textarea rows={2} value={closure.notes||''} onChange={e=>setClosure(c=>({...c, notes:e.target.value}))} style={{...inputStyle, resize:'vertical'}}/>
                        </div>
                    </div>
                    <div style={{display:'flex', gap:'8px', marginTop:'14px'}}>
                        <button onClick={saveCount} disabled={busy} style={{...btnStyle, marginTop:0, backgroundColor:theme.info}}>Save Count</button>
                        <button onClick={closeDay} disabled={busy} style={{...btnStyle, marginTop:0, backgroundColor:theme.success}}>
                            <CheckCircle2 size={14} style={{verticalAlign:'middle', marginRight:'6px'}}/>Close Day
                        </button>
                    </div>
                </div>
            )}

            {locked && closure.notes && (
                <div style={{...cardStyle, marginTop:'14px', backgroundColor:'#f8fafc'}}>
                    <strong style={{color:theme.textMuted, fontSize:'0.82rem'}}>Notes:</strong>
                    <div style={{marginTop:'4px', color:theme.navy}}>{closure.notes}</div>
                    {closure.reopenedAt && (
                        <div style={{marginTop:'10px', padding:'8px 12px', backgroundColor:'#fef3c7', borderRadius:'6px', fontSize:'0.82rem', color:'#92400e'}}>
                            Reopened at {new Date(closure.reopenedAt).toLocaleString('en-IN')} by {closure.reopenedBy} — {closure.reopenReason}
                        </div>
                    )}
                </div>
            )}

            {/* Petty form modal */}
            {pettyForm && (
                <div style={{position:'fixed', inset:0, backgroundColor:'rgba(15,23,42,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999}} onClick={()=>setPettyForm(null)}>
                    <div onClick={e=>e.stopPropagation()} style={{backgroundColor:'white', borderRadius:'14px', padding:'24px', width:'440px', maxWidth:'95vw'}}>
                        <h3 style={{margin:'0 0 14px', color:theme.navy}}>Add Petty Cash Entry</h3>
                        <label style={labelStyle}>Category</label>
                        <select value={pettyForm.category} onChange={e=>setPettyForm({...pettyForm, category:e.target.value})} style={inputStyle}>
                            <option value="expense">Expense (cash out)</option>
                            <option value="bank-deposit">Bank Deposit (cash out)</option>
                            <option value="withdrawal">Owner Withdrawal</option>
                            <option value="refund">Refund (cash out)</option>
                            <option value="other">Other</option>
                        </select>
                        <label style={labelStyle}>Amount (₹) *</label>
                        <input type="number" value={pettyForm.amount} onChange={e=>setPettyForm({...pettyForm, amount:Number(e.target.value)||0})} style={inputStyle}/>
                        <label style={labelStyle}>Description *</label>
                        <input value={pettyForm.description} onChange={e=>setPettyForm({...pettyForm, description:e.target.value})} style={inputStyle}/>
                        <label style={labelStyle}>Recipient / Payee</label>
                        <input value={pettyForm.recipient} onChange={e=>setPettyForm({...pettyForm, recipient:e.target.value})} style={inputStyle}/>
                        <div style={{display:'flex', gap:'8px', marginTop:'12px'}}>
                            <button onClick={()=>setPettyForm(null)} style={{...btnStyle, marginTop:0, backgroundColor:theme.textMuted, flex:1}}>Cancel</button>
                            <button onClick={savePetty} style={{...btnStyle, marginTop:0, flex:2}}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const Row = ({ label, value, big, neg }) => (
    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', paddingVertical:4, marginBottom:'4px'}}>
        <span style={{color: big?theme.navy:theme.textMuted, fontSize: big?'0.92rem':'0.84rem', fontWeight: big?'700':'500'}}>{label}</span>
        <span style={{color: neg?theme.danger:theme.navy, fontSize:'0.9rem'}}>{value}</span>
    </div>
);
